import React, { useEffect, useRef, useState } from 'react';
import { TailSpin } from 'react-loading-icons';
import {
  getAnalyticsEventContext,
  getCurrentUser,
  logAnalyticsEvent,
  persistPreparedReferencePhoto,
  prepareReferencePhotoUrl,
  validateSelectedReferencePhotoFile,
} from '../Firebase';
import {
  isSupportedImageFile,
  normalizeUploadImageFile,
  uploadImageNormalizationConfig,
} from '../imageNormalization';

const REQUIRED_PHOTOS = 3;
const MAX_PHOTOS = 3;
const INPUT_ACCEPT_VALUE = 'image/*,.heic,.heif,.heics,.heifs,.jpg,.jpeg,.png,.webp,.avif';

const PHOTO_PREFIX_PATTERN = /^Photo\s+\d+\s+/i;

const formatPhotoLabel = (indices) => {
  const photoNumbers = indices.map((index) => index + 1);

  if (photoNumbers.length === 1) {
    return `Photo ${photoNumbers[0]}`;
  }

  if (photoNumbers.length === 2) {
    return `Photos ${photoNumbers[0]} and ${photoNumbers[1]}`;
  }

  return `Photos ${photoNumbers.slice(0, -1).join(', ')}, and ${photoNumbers[photoNumbers.length - 1]}`;
};

const formatGroupedValidationMessage = (message, photoCount) => {
  if (photoCount <= 1) {
    return message;
  }

  return message
    .replace(/Please upload a clearer photo of your ([a-z]+)\./i, 'Please upload clearer photos of your $1.')
    .replace(/Please upload a photo with only your ([a-z]+) clearly visible\./i, 'Please upload photos with only your $1 clearly visible.')
    .replace(/Please upload a different photo\./i, 'Please upload different photos.');
};

const PhotoUploader = ({ familyMembers, onUpload, onSubmit, petType, docId = '' }) => {
  const [currentMemberIndex, setCurrentMemberIndex] = useState(0);
  const [photosMap, setPhotosMap] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [isMobileUploadUi, setIsMobileUploadUi] = useState(false);
  const [showLoadingUI, setShowLoadingUI] = useState(false);
  const [isPreparingPhotos, setIsPreparingPhotos] = useState(false);
  const [selectionError, setSelectionError] = useState('');
  const [photoValidationErrors, setPhotoValidationErrors] = useState({});
  const [photoValidationPending, setPhotoValidationPending] = useState({});
  const photosMapRef = useRef(photosMap);

  const currentMemberName = familyMembers[currentMemberIndex]?.name;

  useEffect(() => {
    photosMapRef.current = photosMap;
  }, [photosMap]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return () => {};
    }

    const mediaQuery = window.matchMedia('(max-width: 767px), (pointer: coarse)');
    const updateMatch = () => {
      setIsMobileUploadUi(mediaQuery.matches);
    };

    updateMatch();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateMatch);
      return () => mediaQuery.removeEventListener('change', updateMatch);
    }

    mediaQuery.addListener(updateMatch);
    return () => mediaQuery.removeListener(updateMatch);
  }, []);

  useEffect(() => () => {
    Object.values(photosMapRef.current || {}).forEach((entry) => {
      (entry?.urls || []).forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    });
  }, []);

  const trackUploadEvent = (eventName, properties = {}) => {
    const analyticsContext = getAnalyticsEventContext(getCurrentUser());
    logAnalyticsEvent({
      analyticsContext,
      eventName,
      properties: {
        screen: 'upload_photos',
        member_name: currentMemberName || '',
        current_member_index: currentMemberIndex,
        ...properties,
      },
    });
  };

  useEffect(() => {
    if (!currentMemberName || photosMap[currentMemberName]) {
      return;
    }

    setPhotosMap((prevMap) => ({
      ...prevMap,
      [currentMemberName]: { files: [], urls: [], validationUrls: [], processedValidationUrls: [] },
    }));
  }, [currentMemberName, photosMap]);

  useEffect(() => {
    setSelectionError('');
    setPhotoValidationErrors({});
    setPhotoValidationPending({});
  }, [currentMemberName]);

  const appendFiles = async (incomingFiles, replacePhotoIndex = null) => {
    if (!currentMemberName || incomingFiles.length === 0) {
      return;
    }

    const existing = photosMap[currentMemberName] || { files: [], urls: [], validationUrls: [], processedValidationUrls: [] };
    const invalidPhotoIndices = Object.keys(photoValidationErrors)
      .map((key) => Number(key))
      .filter((index) => Number.isInteger(index) && index >= 0)
      .sort((left, right) => left - right);
    const replacementIndices = replacePhotoIndex !== null
      ? [replacePhotoIndex]
      : (
        existing.files.length >= MAX_PHOTOS && invalidPhotoIndices.length > 0
          ? invalidPhotoIndices
          : []
      );
    const replacementSlots = replacementIndices.length;

    setSelectionError('');
    setPhotoValidationErrors((prevErrors) => {
      if (replacementSlots === 0) {
        return prevErrors;
      }

      const nextErrors = { ...prevErrors };
      replacementIndices.forEach((index) => {
        delete nextErrors[index];
      });
      return nextErrors;
    });
    setPhotoValidationPending((prevPending) => {
      if (replacementSlots === 0) {
        return prevPending;
      }

      const nextPending = { ...prevPending };
      replacementIndices.forEach((index) => {
        delete nextPending[index];
      });
      return nextPending;
    });

    if (replacementSlots > 0 && docId) {
      replacementIndices.forEach((index) => {
        void persistPreparedReferencePhoto({
          docId,
          photoIndex: index,
          validationUrl: '',
          processedReferencePhotoUrl: '',
          petType,
        }).catch((persistError) => {
          console.warn('Failed to clear prepared reference slot:', persistError);
        });
      });
    }

    const remainingSlots = replacementSlots > 0 ? replacementSlots : Math.max(0, MAX_PHOTOS - existing.files.length);
    const imageFiles = incomingFiles.filter((file) => isSupportedImageFile(file));
    const filesToAdd = imageFiles.slice(0, remainingSlots);
    const ignoredCount = Math.max(0, imageFiles.length - filesToAdd.length);

    if (filesToAdd.length === 0) {
      setSelectionError(
        replacementSlots > 0
          ? `Choose up to ${replacementSlots} replacement photo${replacementSlots === 1 ? '' : 's'} for the highlighted slot${replacementSlots === 1 ? '' : 's'}.`
          : `You can upload exactly ${REQUIRED_PHOTOS} photos.`
      );
      return;
    }

    setIsPreparingPhotos(true);

    try {
      const normalizedFiles = [];
      const skippedMessages = [];

      for (const file of filesToAdd) {
        try {
          const normalizedFile = await normalizeUploadImageFile(file, uploadImageNormalizationConfig);
          normalizedFiles.push(normalizedFile);
        } catch (error) {
          skippedMessages.push(String(error?.message || error));
        }
      }

      if (normalizedFiles.length === 0) {
        setSelectionError(skippedMessages[0] || 'Could not prepare these photos for upload.');
        return;
      }

      trackUploadEvent('fluffball-upload-photos-added', {
        added_count: normalizedFiles.length,
        attempted_count: filesToAdd.length,
        skipped_count: filesToAdd.length - normalizedFiles.length,
        total_selected: replacementSlots > 0 ? existing.files.length : existing.files.length + normalizedFiles.length,
        ...(replacementSlots > 0 ? { replaced_indices: replacementIndices.slice(0, normalizedFiles.length) } : {}),
      });

      const validationTargets = [];
      const nextFiles = [...existing.files];
      const nextUrls = [...existing.urls];
      const nextValidationUrls = Array.isArray(existing.validationUrls) ? [...existing.validationUrls] : [];
      const nextProcessedValidationUrls = Array.isArray(existing.processedValidationUrls)
        ? [...existing.processedValidationUrls]
        : [];
      if (replacementSlots > 0) {
        normalizedFiles.forEach((file, offset) => {
          const targetIndex = replacementIndices[offset];
          const replacementUrl = URL.createObjectURL(file);
          const existingUrl = nextUrls[targetIndex];
          if (existingUrl) {
            URL.revokeObjectURL(existingUrl);
          }
          nextFiles[targetIndex] = file;
          nextUrls[targetIndex] = replacementUrl;
          nextValidationUrls[targetIndex] = '';
          nextProcessedValidationUrls[targetIndex] = '';
          validationTargets.push({ file, index: targetIndex });
        });
      } else {
        normalizedFiles.forEach((file, offset) => {
          nextFiles.push(file);
          nextUrls.push(URL.createObjectURL(file));
          nextValidationUrls.push('');
          nextProcessedValidationUrls.push('');
          validationTargets.push({
            file,
            index: existing.files.length + offset,
          });
        });
      }
      setPhotosMap((prevMap) => ({
        ...prevMap,
        [currentMemberName]: {
          files: nextFiles,
          urls: nextUrls,
          validationUrls: nextValidationUrls,
          processedValidationUrls: nextProcessedValidationUrls,
        },
      }));
      setPhotoValidationPending((prevPending) => {
        const nextPending = { ...prevPending };
        validationTargets.forEach(({ index }) => {
          nextPending[index] = true;
        });
        return nextPending;
      });
      setIsPreparingPhotos(false);

      await Promise.all(validationTargets.map(async ({ file, index }) => {
        try {
          const { validationUrl, uploadId } = await validateSelectedReferencePhotoFile({
            file,
            petType,
            photoIndex: index,
          });
          trackUploadEvent('fluffball-upload-photo-validated', {
            photo_index: index,
            pet_type: petType || '',
            validation_url_present: Boolean(validationUrl),
          });
          setPhotosMap((prevMap) => {
            const memberPhotos = prevMap[currentMemberName];
            if (!memberPhotos) {
              return prevMap;
            }

            const updatedValidationUrls = Array.isArray(memberPhotos.validationUrls)
              ? [...memberPhotos.validationUrls]
              : [];
            updatedValidationUrls[index] = validationUrl;

            return {
              ...prevMap,
              [currentMemberName]: {
                ...memberPhotos,
                validationUrls: updatedValidationUrls,
              },
            };
          });

          void prepareReferencePhotoUrl({
            imageUrl: validationUrl,
            petType,
            photoIndex: index,
            uploadId,
          })
            .then((payload) => {
              const processedReferencePhotoUrl = String(payload?.processed_reference_photo_url || '').trim();
              if (!processedReferencePhotoUrl) {
                return;
              }

              setPhotosMap((prevMap) => {
                const memberPhotos = prevMap[currentMemberName];
                if (!memberPhotos) {
                  return prevMap;
                }

                const currentValidationUrls = Array.isArray(memberPhotos.validationUrls)
                  ? memberPhotos.validationUrls
                  : [];
                if (currentValidationUrls[index] !== validationUrl) {
                  return prevMap;
                }

                const updatedProcessedValidationUrls = Array.isArray(memberPhotos.processedValidationUrls)
                  ? [...memberPhotos.processedValidationUrls]
                  : [];
                updatedProcessedValidationUrls[index] = processedReferencePhotoUrl;

                return {
                  ...prevMap,
                  [currentMemberName]: {
                    ...memberPhotos,
                    processedValidationUrls: updatedProcessedValidationUrls,
                  },
                };
              });

              if (docId) {
                void persistPreparedReferencePhoto({
                  docId,
                  photoIndex: index,
                  validationUrl,
                  processedReferencePhotoUrl,
                  petType,
                }).catch((persistError) => {
                  console.warn('Failed to persist prepared reference photo:', persistError);
                });
              }
            })
            .catch((prepareError) => {
              console.warn('Background reference-photo preparation failed:', prepareError);
            });

          setPhotoValidationErrors((prevErrors) => {
            const nextErrors = { ...prevErrors };
            delete nextErrors[index];
            return nextErrors;
          });
        } catch (error) {
          const validationMessage = String(error?.message || error || '').trim() || 'This photo needs to be replaced.';
          trackUploadEvent('fluffball-upload-photo-validation-failed', {
            photo_index: index,
            pet_type: petType || '',
            error_message: validationMessage,
          });
          setPhotosMap((prevMap) => {
            const memberPhotos = prevMap[currentMemberName];
            if (!memberPhotos) {
              return prevMap;
            }

            const updatedValidationUrls = Array.isArray(memberPhotos.validationUrls)
              ? [...memberPhotos.validationUrls]
              : [];
            updatedValidationUrls[index] = '';

            return {
              ...prevMap,
              [currentMemberName]: {
                ...memberPhotos,
                validationUrls: updatedValidationUrls,
                processedValidationUrls: Array.isArray(memberPhotos.processedValidationUrls)
                  ? memberPhotos.processedValidationUrls.map((url, urlIndex) => (urlIndex === index ? '' : url))
                  : [],
              },
            };
          });
          setPhotoValidationErrors((prevErrors) => ({
            ...prevErrors,
            [index]: validationMessage,
          }));
        } finally {
          setPhotoValidationPending((prevPending) => {
            const nextPending = { ...prevPending };
            delete nextPending[index];
            return nextPending;
          });
        }
      }));

      if (ignoredCount > 0) {
        setSelectionError(
          replacementSlots > 0
            ? `Only ${replacementSlots} replacement photo${replacementSlots === 1 ? '' : 's'} can be selected. Extra photos were skipped.`
            : `Only ${MAX_PHOTOS} photos can be uploaded. Extra photos were skipped.`
        );
      } else if (skippedMessages.length > 0) {
        setSelectionError(skippedMessages[0]);
      }
    } finally {
      setIsPreparingPhotos(false);
    }
  };

  const handlePhotoChange = async (event) => {
    const files = Array.from(event.target.files || []);
    await appendFiles(files);
    event.target.value = '';
  };

  const handleDeletePhoto = (photoIndex) => {
    if (!currentMemberName) {
      return;
    }

    const existing = photosMap[currentMemberName];
    if (!existing) {
      return;
    }

    trackUploadEvent('fluffball-upload-photo-removed', {
      removed_index: photoIndex,
      total_selected: Math.max(0, existing.files.length - 1),
    });
    setPhotoValidationErrors((prevErrors) => {
      const nextErrors = {};
      Object.entries(prevErrors).forEach(([key, value]) => {
        const numericIndex = Number(key);
        if (!Number.isInteger(numericIndex) || numericIndex === photoIndex) {
          return;
        }
        nextErrors[numericIndex > photoIndex ? numericIndex - 1 : numericIndex] = value;
      });
      return nextErrors;
    });
    setPhotoValidationPending((prevPending) => {
      const nextPending = {};
      Object.entries(prevPending).forEach(([key, value]) => {
        const numericIndex = Number(key);
        if (!Number.isInteger(numericIndex) || numericIndex === photoIndex) {
          return;
        }
        nextPending[numericIndex > photoIndex ? numericIndex - 1 : numericIndex] = value;
      });
      return nextPending;
    });

    if (docId) {
      void persistPreparedReferencePhoto({
        docId,
        photoIndex,
        validationUrl: '',
        processedReferencePhotoUrl: '',
        petType,
      }).catch((persistError) => {
        console.warn('Failed to clear deleted prepared reference slot:', persistError);
      });
    }

    setPhotosMap((prevMap) => {
      const currentExisting = prevMap[currentMemberName];
      if (!currentExisting) {
        return prevMap;
      }

      const removedUrl = currentExisting.urls[photoIndex];
      if (removedUrl) {
        URL.revokeObjectURL(removedUrl);
      }

      return {
        ...prevMap,
        [currentMemberName]: {
          files: currentExisting.files.filter((_, index) => index !== photoIndex),
          urls: currentExisting.urls.filter((_, index) => index !== photoIndex),
          validationUrls: (Array.isArray(currentExisting.validationUrls) ? currentExisting.validationUrls : [])
            .filter((_, index) => index !== photoIndex),
          processedValidationUrls: (Array.isArray(currentExisting.processedValidationUrls) ? currentExisting.processedValidationUrls : [])
            .filter((_, index) => index !== photoIndex),
        },
      };
    });
  };

  const handleDrag = (event) => {
    if (isMobileUploadUi) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true);
    } else if (event.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (event) => {
    if (isMobileUploadUi) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    const files = Array.from(event.dataTransfer.files || []).filter((file) => isSupportedImageFile(file));
    await appendFiles(files);
  };

  const handleContinue = async () => {
    const uploadedCount = photosMap[currentMemberName]?.files?.length || 0;
    setSelectionError('');
    trackUploadEvent('fluffball-upload-continue-clicked', {
      uploaded_count: uploadedCount,
      requirement_met: uploadedCount === REQUIRED_PHOTOS,
    });
    if (uploadedCount !== REQUIRED_PHOTOS) {
      return;
    }

    setShowLoadingUI(true);

    try {
      if (currentMemberIndex < familyMembers.length - 1) {
        setCurrentMemberIndex((prevIndex) => prevIndex + 1);
        setShowLoadingUI(false);
        return;
      }

      const userId = await onUpload(familyMembers, photosMap);
      trackUploadEvent('fluffball-upload-succeeded', {
        uploaded_count: uploadedCount,
        doc_id: userId,
      });
      await onSubmit(userId);
    } catch (error) {
      console.error('Error uploading photos:', error);
      const errorMessage = String(error?.message || error || '').trim();
      trackUploadEvent('fluffball-upload-failed', {
        uploaded_count: uploadedCount,
        error_message: errorMessage,
      });
      setShowLoadingUI(false);

      if (errorMessage.toLowerCase().includes('not signed in')) {
        setSelectionError('Please sign in with Google before uploading photos.');
        alert('Please sign in with Google before uploading photos.');
      } else if (Array.isArray(error?.photoValidationErrors) && error.photoValidationErrors.length > 0) {
        const nextPhotoValidationErrors = {};
        error.photoValidationErrors.forEach((detail) => {
          const detailIndex = Number(detail?.index);
          const detailMessage = String(detail?.message || '').trim();
          if (Number.isInteger(detailIndex) && detailIndex >= 0 && detailMessage) {
            nextPhotoValidationErrors[detailIndex] = detailMessage;
          }
        });
        const invalidPhotoCount = Object.keys(nextPhotoValidationErrors).length;
        setPhotoValidationErrors(nextPhotoValidationErrors);
        setSelectionError(
          invalidPhotoCount > 1
            ? `${invalidPhotoCount} photos need to be replaced before you continue.`
            : '1 photo needs to be replaced before you continue.'
        );
      } else {
        setSelectionError(errorMessage || 'There was an error uploading your photos. Please try again.');
      }
    }
  };

  const uploadedCount = photosMap[currentMemberName]?.files?.length || 0;
  const validatedCount = (photosMap[currentMemberName]?.validationUrls || [])
    .filter((url) => typeof url === 'string' && url.trim())
    .length;
  const invalidPhotoCount = Object.keys(photoValidationErrors).length;
  const pendingPhotoCount = Object.keys(photoValidationPending).length;
  const invalidPhotoEntries = Object.entries(photoValidationErrors)
    .map(([key, value]) => ({
      index: Number(key),
      message: String(value || '').trim(),
    }))
    .filter(({ index, message }) => Number.isInteger(index) && index >= 0 && message)
    .sort((left, right) => left.index - right.index);
  const groupedInvalidPhotoMessages = Array.from(
    invalidPhotoEntries.reduce((groups, { index, message }) => {
      const normalizedMessage = message.replace(PHOTO_PREFIX_PATTERN, '').trim() || message;
      const existingGroup = groups.get(normalizedMessage) || {
        indices: [],
        message: normalizedMessage,
        usedPhotoPrefix: PHOTO_PREFIX_PATTERN.test(message),
      };
      existingGroup.indices.push(index);
      groups.set(normalizedMessage, existingGroup);
      return groups;
    }, new Map())
  ).map(([, group]) => {
    const photoLabel = formatPhotoLabel(group.indices);
    const formattedMessage = formatGroupedValidationMessage(group.message, group.indices.length);
    return {
      ...group,
      text: group.usedPhotoPrefix ? `${photoLabel} ${formattedMessage}` : `${photoLabel}: ${formattedMessage}`,
    };
  });
  const statusMessage = invalidPhotoCount > 0
    ? (
      invalidPhotoCount === 1
        ? groupedInvalidPhotoMessages[0]?.text || '1 photo needs to be replaced before you continue.'
        : `${invalidPhotoCount} photos need to be replaced before you continue.`
    )
    : selectionError;
  const canContinue = uploadedCount === REQUIRED_PHOTOS
    && validatedCount === REQUIRED_PHOTOS
    && !isPreparingPhotos
    && invalidPhotoCount === 0
    && pendingPhotoCount === 0;
  const canChoosePhotos = !showLoadingUI
    && !isPreparingPhotos
    && pendingPhotoCount === 0
    && (uploadedCount < MAX_PHOTOS || invalidPhotoCount > 0);

  return (
    <div className="max-w-3xl lg:max-w-4xl mx-auto pb-4">
      {showLoadingUI || isPreparingPhotos ? (
        <div className="fixed inset-0 bg-white/90 z-20 flex items-center justify-center">
          <TailSpin stroke="#8B5CF6" speed={0.75} width="48" />
        </div>
      ) : null}

      <div className="text-center mb-5 lg:mb-6">
        <h2 className="text-3xl sm:text-4xl font-semibold text-purple-700">Upload Your Pet Photos</h2>
      </div>

      <div className="bg-white border border-purple-100 rounded-3xl p-5 sm:p-7 lg:p-8 shadow-sm">
        <div
          className={`border-2 ${isMobileUploadUi ? 'border-solid' : 'border-dashed'} rounded-2xl p-6 lg:p-8 text-center transition-colors ${
            !isMobileUploadUi && dragActive
              ? 'border-purple-400 bg-purple-50'
              : 'border-purple-200 hover:border-purple-300'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {isMobileUploadUi ? (
            <p className="text-base lg:text-lg text-purple-700 mb-3">Choose 3 clear photos of your pet.</p>
          ) : (
            <p className="text-base lg:text-lg text-purple-700 mb-3">Drag and drop photos here, or</p>
          )}
          <label className={`inline-flex items-center justify-center text-white text-base lg:text-lg font-medium py-3 px-5 rounded-lg transition-colors ${
            canChoosePhotos
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 cursor-pointer'
              : 'bg-purple-200 text-purple-50 cursor-not-allowed'
          }`}>
            Choose photos
            <input
              key={currentMemberName}
              className="hidden"
              type="file"
              multiple
              accept={INPUT_ACCEPT_VALUE}
              onChange={handlePhotoChange}
              disabled={!canChoosePhotos}
            />
          </label>
        </div>
        {statusMessage ? (
          <div
            className={`mt-3 rounded-xl px-4 py-3 ${
              invalidPhotoCount > 0
                ? 'border border-red-200 bg-red-50'
                : 'border border-red-200 bg-red-50'
            }`}
          >
            <p
              className={`text-sm font-medium ${
                invalidPhotoCount > 0
                  ? 'text-red-700'
                  : 'text-red-700'
              } lg:text-lg`}
            >
              {statusMessage}
            </p>
            {invalidPhotoCount > 1 ? (
              <div className="mt-2 space-y-1">
                {groupedInvalidPhotoMessages.map(({ text, indices }) => (
                  <p key={`photo-validation-banner-${indices.join('-')}`} className="text-sm lg:text-base leading-6 text-red-600">
                    {text}
                  </p>
                ))}
              </div>
            ) : null}
            {invalidPhotoCount > 0 ? (
              <p className="mt-2 text-sm lg:text-base text-red-600">
                Replace the highlighted photo{invalidPhotoCount > 1 ? 's' : ''} below.
              </p>
            ) : null}
          </div>
        ) : null}

        {uploadedCount > 0 ? (
          <div className="mt-4 grid grid-cols-3 gap-2">
            {photosMap[currentMemberName]?.urls.map((photoUrl, index) => (
              <div key={`${photoUrl}-${index}`}>
                <div
                  className={`relative overflow-hidden rounded-lg border aspect-square bg-slate-100 ${
                    photoValidationErrors[index]
                      ? 'border-red-500 ring-2 ring-red-200'
                      : photoValidationPending[index]
                        ? 'border-purple-400 ring-2 ring-purple-100'
                      : 'border-gray-200'
                  }`}
                >
                  <img src={photoUrl} alt="Uploaded" className="w-full h-full object-cover" />
                  {photoValidationPending[index] ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
                      <div className="flex items-center justify-center rounded-full bg-white/90 p-3 shadow-sm">
                        <TailSpin stroke="#8B5CF6" speed={0.75} width="20" />
                      </div>
                    </div>
                  ) : null}
                  <div className="absolute inset-x-0 top-0 flex items-start justify-end p-2">
                    <button
                      className="flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-base leading-none text-purple-700 border border-purple-100 hover:bg-white"
                      onClick={() => handleDeletePhoto(index)}
                      disabled={showLoadingUI || isPreparingPhotos || pendingPhotoCount > 0}
                      aria-label="Delete photo"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <button
          className={`mt-5 w-full py-3 px-4 rounded-lg text-base lg:text-lg font-semibold transition-colors ${
            canContinue
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
              : 'bg-purple-100 text-purple-400 cursor-not-allowed'
          }`}
          onClick={handleContinue}
          disabled={!canContinue || showLoadingUI || isPreparingPhotos}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default PhotoUploader;
